import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * SignaturePad — lightweight touch/mouse signature canvas.
 * Uses pointer events so it works on phones, tablets and desktop equally.
 * Exposes `getDataUrl()` and `clear()` via the ref passed to it.
 */
export const SignaturePad = React.forwardRef(({ height = 180, className = '' }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasStrokes, setHasStrokes] = useState(false);

  // Handle high-DPR rendering so the stroke is crisp on retina/mobile displays
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#0f172a';
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = getPos(e);
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (!hasStrokes) setHasStrokes(true);
  };
  const onUp = () => { drawing.current = false; };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasStrokes(false);
  }, []);

  const getDataUrl = useCallback(() => {
    if (!hasStrokes) return null;
    return canvasRef.current.toDataURL('image/png');
  }, [hasStrokes]);

  React.useImperativeHandle(ref, () => ({ clear, getDataUrl, hasStrokes }), [clear, getDataUrl, hasStrokes]);

  return (
    <div className={`relative border border-dashed border-[#cbd5e1] rounded-md bg-white ${className}`}>
      <canvas
        ref={canvasRef}
        style={{ height, width: '100%', touchAction: 'none', display: 'block' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onPointerLeave={onUp}
        data-testid="signature-canvas"
      />
      {!hasStrokes && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-[#94a3b8]">
          Sign here
        </div>
      )}
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';
